import './performanceCheck'
import React, { useEffect, useRef, useMemo, useState } from 'react'
import { useFlagship, HitType } from '@flagship.io/react-sdk'
import {
  withSsrLocaleData,
  Types,
  useAnalytic,
  pushFlagsDataInGTM,
  GROWTHBOOK_FLAGS,
  CookieUtils
} from '@mafc/common'
import smartlookClient from 'smartlook-client'
import Layout from '../../components/Layout'
import ErrorBoundary from '../../components/ErrorBoundary'
import { isServer } from '../../utils'
import { withWrapper } from '../../config/withStore'
import { useFeature } from 'index'
interface LangResources {
  en: any
  ar: any
}
interface AppProps {
  pageProps?: {}
  appConfig: Types.IAppConfig
  baseTheme?: any
  withLayout?: boolean
  children?: React.ReactNode
}

interface AppShellProps extends Omit<AppProps, 'pageProps'> {
  resources: LangResources
  isSSR?: boolean
}

type IState = { triggered: boolean }

const MainContent: React.FunctionComponent<{
  children: React.ReactNode
  language: string
  baseTheme: any
  withLayout?: boolean
}> = ({ children, baseTheme, language, withLayout = true }) => {
  return (
    <Layout withLayout={withLayout} baseTheme={baseTheme} language={language}>
      <ErrorBoundary>{children}</ErrorBoundary>
    </Layout>
  )
}

const MainContentMemoize = React.memo(
  MainContent,
  ({ language: prevLanguage }, { language: nextLanguage }) => {
    return prevLanguage !== nextLanguage
  }
)

const App: React.FunctionComponent<AppProps> = ({
  children,
  appConfig,
  baseTheme,
  withLayout
}) => {
  const isFLagshipEnabled =
    useFeature(GROWTHBOOK_FLAGS.flagshipEnabled)?.on || false

  const isNewListingPageEnabled =
    useFeature('flutter.listing_page.enabled')?.value || false

  const isNewPDPEnabled = useFeature('revamp.pdp.enabled')?.on || false

  const isNewHomeEnabled = useFeature('revamp.home.enabled')?.on || false

  const [triggeredPageViewGTM, settriggeredPageViewGTM] = useState<IState>(
    () => ({
      triggered: false
    })
  )
  const { language = 'en' } = appConfig
  const { hit: fsHit, visitorId } = useFlagship()

  const { getGTMPageData } = useAnalytic()
  const pageData = useMemo(() => getGTMPageData(), [getGTMPageData])

  useEffect(() => {
    const smartLookProjectKey = process.env.REACT_APP_SMARTLOOK_PROJECT_KEY
    if (smartLookProjectKey) {
      /* @ts-ignore */
      const options: any = {
        region: 'eu',
        interceptors: {
          url: (data) => {
            const queryIndex = data.url.indexOf('?')
            if (queryIndex > -1) {
              data.url = data.url.slice(0, queryIndex) + '?query=[OBSCURED]'
            }
          }
        }
      }
      smartlookClient.init(`${smartLookProjectKey}`, {
        ...options
      })
    } else {
    }
  }, [])

  useEffect(() => {
    if (isNewListingPageEnabled) {
      CookieUtils.setCustomCookie('isNewPLP', true, 260000000)
    } else {
      CookieUtils.setCustomCookie('isNewPLP', false, -1)
    }
  }, [isNewListingPageEnabled])

  useEffect(() => {
    if (isNewPDPEnabled) {
      CookieUtils.setCustomCookie('isNewPDP', true, 260000000)
    } else {
      CookieUtils.setCustomCookie('isNewPDP', false, -1)
    }
  }, [isNewPDPEnabled])

  useEffect(() => {
    if (isNewHomeEnabled) {
      CookieUtils.setCustomCookie('isNewHome', true, 260000000)
    } else {
      CookieUtils.setCustomCookie('isNewHome', false, -1)
    }
  }, [isNewHomeEnabled])

  useEffect(() => {
    const { dataLayer } = window as any
    if (pageData && dataLayer && !triggeredPageViewGTM.triggered) {
      settriggeredPageViewGTM({ triggered: true })
      dataLayer.push(pageData)
    }
  }, [pageData, triggeredPageViewGTM])

  useEffect(() => {
    if (visitorId) {
      fsHit.send({
        type: HitType.PAGE,
        documentLocation: window.location.href
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitorId])

  useEffect(() => {
    const { dataLayer } = window as any

    const {
      flagShip: { initialFlagsData = [] } = { initialFlagsData: [] }
    } = appConfig
    if (dataLayer && initialFlagsData && isFLagshipEnabled && visitorId) {
      pushFlagsDataInGTM(visitorId, initialFlagsData)
    }
  }, [visitorId, appConfig, isFLagshipEnabled])

  return (
    <MainContentMemoize
      withLayout={withLayout}
      language={language}
      baseTheme={baseTheme}
    >
      {children}
    </MainContentMemoize>
  )
}

const MemoizedApp = React.memo(
  App,
  (
    { appConfig: { language: prevLanguage, storeId: prevStoreId } },
    { appConfig: { language: nextLanguage, storeId: nextStoreId } }
  ) => {
    return prevLanguage !== nextLanguage || prevStoreId !== nextStoreId
  }
)

const extendedApp = (
  lng: string,
  resources: LangResources = { en: {}, ar: {} }
) => withSsrLocaleData(MemoizedApp, { resources, lng })

const LocalisedApp: React.FunctionComponent<AppShellProps> = (props) => {
  const { resources, appConfig = { language: 'en' } } = props
  const { language } = appConfig
  const MainApp = useRef<{
    [key: string]: React.NamedExoticComponent<AppProps>
  }>({})
  const resource = useRef(resources)
  useEffect(() => {
    if (!isServer()) {
      window.document.dir = language === 'ar' ? 'rtl' : 'ltr'
    }
  }, [language])
  const MemoizedApp = useMemo(() => {
    if (language && !MainApp.current[language]) {
      MainApp.current[language] = extendedApp(language, resource.current)
    }
    return MainApp.current[language] || null
  }, [language, resource])
  if (!MemoizedApp) {
    return null
  }
  return <MemoizedApp {...props} appConfig={appConfig} />
}

const MemoizedLocalizedApp = React.memo(
  LocalisedApp,
  (
    { appConfig: { language: prevLang } },
    { appConfig: { language: nextLang } }
  ) => {
    return prevLang !== nextLang
  }
)

const wrapper = withWrapper(MemoizedLocalizedApp)

const csrWrapper = withWrapper(MemoizedLocalizedApp, false)

export const withReduxWrapper = wrapper.withReduxWrapper

export const withCSRWrapper = csrWrapper.withReduxWrapper

export const smartlook = smartlookClient

export default wrapper
